import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Header: React.FC = () => {

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [registrationEnabled, setRegistrationEnabled] = React.useState(false);

  // Check registration status on component mount
  React.useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await api.get('/auth/registration-status');
        setRegistrationEnabled(response.data.enabled);
      } catch (error) {
        setRegistrationEnabled(false);
      }
    };
    
    checkRegistrationStatus();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="bg-white/95 backdrop-blur-lg shadow-xl border-b border-warm-300/50 sticky top-0 z-50">
      <nav className="container mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-4 group" onClick={scrollToTop}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <img src="/img/core-img/logo-brown.png" alt="SoundX724 Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-secondary-800 via-primary-700 to-warm-800 bg-clip-text text-transparent font-elegant">
                SoundX724
              </h1>
              <p className="text-sm text-primary-600 font-sanskrit tracking-wide">यत्र सङ्गीतं तत्र आत्मनः|</p>
            </div>
          </Link>

          {/* Navigation Menu */}
          <div className="hidden lg:flex items-center space-x-12">
            <Link 
              to="/" 
              className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 relative group text-lg"
              onClick={scrollToTop}
            >
              Home
              <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-primary-600 to-warm-600 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/events" 
              className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 relative group text-lg"
              onClick={scrollToTop}
            >
              Events
              <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-primary-600 to-warm-600 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/artist-submission" 
              className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 relative group text-lg"
              onClick={scrollToTop}
            >
              Submit Art
              <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-primary-600 to-warm-600 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/manage-booking" 
              className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 relative group text-lg"
              onClick={scrollToTop}
            >
              My Booking
              <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-primary-600 to-warm-600 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/about" 
              className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 relative group text-lg"
              onClick={scrollToTop}
            >
              About
              <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-primary-600 to-warm-600 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </Link>
            
            {user ? (
              <div className="flex items-center space-x-6">
                <Link 
                  to="/dashboard" 
                  className="bg-gradient-to-r from-accent-400 to-accent-500 hover:from-accent-500 hover:to-accent-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                  onClick={scrollToTop}
                >
                  Dashboard
                </Link>
                <div className="text-right">
                  <p className="text-secondary-800 font-semibold text-sm">Welcome back</p>
                  <p className="text-primary-700 font-bold">{user.username || user.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-secondary-600 to-secondary-700 hover:from-secondary-700 hover:to-secondary-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-secondary-700 hover:text-primary-800 font-semibold transition-all duration-300 px-4 py-2 rounded-lg hover:bg-warm-100"
                  onClick={scrollToTop}
                >
                  Login
                </Link>
                {registrationEnabled && (
                  <Link
                    to="/register"
                    className="bg-gradient-to-r from-primary-600 via-primary-700 to-warm-700 hover:from-primary-700 hover:via-primary-800 hover:to-warm-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 border border-primary-500/20"
                    onClick={scrollToTop}
                  >
                    Sign Up
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              className="text-secondary-700 hover:text-primary-800 focus:outline-none p-2 rounded-lg hover:bg-warm-100 transition-all duration-300"
              onClick={() => setMobileMenuOpen(prev => !prev)}
              aria-label="Open menu"
            >
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full bg-white shadow-xl rounded-b-xl z-40 py-6 px-8 flex flex-col gap-6 animate-fade-in">
            <Link to="/" className="text-secondary-700 hover:text-primary-800 font-semibold text-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Home</Link>
            <Link to="/events" className="text-secondary-700 hover:text-primary-800 font-semibold text-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Events</Link>
            <Link to="/artist-submission" className="text-secondary-700 hover:text-primary-800 font-semibold text-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Submit Art</Link>
            <Link to="/manage-booking" className="text-secondary-700 hover:text-primary-800 font-semibold text-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>My Booking</Link>
            <Link to="/about" className="text-secondary-700 hover:text-primary-800 font-semibold text-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>About</Link>
            {user ? (
              <>
                <Link to="/dashboard" className="bg-gradient-to-r from-accent-400 to-accent-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Dashboard</Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="bg-gradient-to-r from-secondary-600 to-secondary-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg"
                >Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-secondary-700 hover:text-primary-800 font-semibold px-4 py-2 rounded-lg" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Login</Link>
                {registrationEnabled && (
                  <Link to="/register" className="bg-gradient-to-r from-primary-600 via-primary-700 to-warm-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg border border-primary-500/20" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Sign Up</Link>
                )}
              </>
            )}
          </div>
        )}
        {/* Decorative border */}
        <div className="mt-5 h-1 bg-gradient-to-r from-primary-400 via-warm-400 to-accent-400 rounded-full opacity-60"></div>
      </nav>
    </header>
  );
};

export default Header;